import React, { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { consentApi } from '../services/api';
import PolicyDocumentContent from './PolicyDocumentContent';
import {
  fetchCurrentDocument,
  PolicyDocument,
  PolicyDocumentType,
} from '../services/policyDocuments';

/**
 * ConsentGate (task 422, explicit-agree dialog task 666) — blocking
 * interstitial for policy consent.
 *
 * Rendered by AuthGate (App.tsx) around everything inside the Router, AFTER
 * the provisioning checks. When the token carries `consent_pending=true`
 * (surfaced as `user.consentPending` by AuthContext), the gate replaces the
 * app with an explicit-agree dialog: the User ticks a never-pre-ticked
 * "I have read and agree…" checkbox, which enables the Agree button. Reading
 * the documents is available, not enforced — this mirrors the approved
 * register-page design (task 665) so the signup and re-acceptance consent
 * surfaces stay one design.
 *
 * The dialog has two panes of local state:
 *  - Checklist — the required documents with version / effective date and an
 *    Open button, the agree checkbox (document names inside it also open the
 *    reader), a disabled-until-checked Agree button, and Sign out.
 *  - Reader — the selected document rendered inline via `PolicyDocumentContent`
 *    inside a fixed-height scroll container, with its own loading / error /
 *    retry state. The checkbox state survives opening and closing the reader.
 *
 * Accepting calls the innovate-service proxy (POST /api/consent/accept), which
 * records consent in AMS against the token subject. A successful response is
 * treated as AUTHORITATIVE FOR THE SESSION: the AMS SPI claims cache (~30s)
 * means freshly refreshed tokens can still carry consent_pending=true, so the
 * gate stores a per-subject acknowledgement in sessionStorage and never
 * re-prompts this browser session — no token refresh is required to proceed.
 */

/** The required policy documents, in display order. */
const REQUIRED_DOCUMENTS: { type: PolicyDocumentType; label: string }[] = [
  { type: 'terms_of_use', label: 'Terms of Use' },
  { type: 'privacy_policy', label: 'Privacy Policy' },
];

interface DocMeta {
  version: string;
  effectiveAt: string;
}

/** sessionStorage key marking this browser session's acceptance, per subject. */
const sessionAckKey = (sub: string) => `consent_session_ack:${sub}`;

/** True when this session has already recorded an acceptance for `sub`. */
const hasSessionAck = (sub: string): boolean => {
  try {
    return sessionStorage.getItem(sessionAckKey(sub)) === 'true';
  } catch {
    return false;
  }
};

const setSessionAck = (sub: string): void => {
  try {
    sessionStorage.setItem(sessionAckKey(sub), 'true');
  } catch {
    // sessionStorage unavailable — the in-memory state still unblocks this mount.
  }
};

/** Render an ISO effectiveAt timestamp as its date part (YYYY-MM-DD). */
const formatEffectiveDate = (effectiveAt: string): string => effectiveAt.slice(0, 10);

/**
 * Reader pane — renders one policy document inline for optional reading.
 *
 * Keyed by document type at the call site so each open remounts fresh.
 * `PolicyDocumentContent` owns the loading / error / retry states; a fetch
 * failure never blocks consent, since agreement is gated on the checkbox alone.
 */
function ReaderPane({
  type,
  label,
  meta,
  onLoaded,
  onBack,
}: {
  type: PolicyDocumentType;
  label: string;
  meta?: DocMeta;
  onLoaded: (type: PolicyDocumentType, document: PolicyDocument) => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-3">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          &larr; Back to documents
        </button>
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
          {meta && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Version {meta.version} — effective {formatEffectiveDate(meta.effectiveAt)}
            </p>
          )}
        </div>
      </div>

      <div
        data-testid="consent-reader-scroll"
        className="mt-4 h-80 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-4 text-left text-sm leading-6 text-gray-700 dark:text-gray-300"
      >
        <PolicyDocumentContent type={type} onLoaded={(document) => onLoaded(type, document)} />
      </div>
    </div>
  );
}

export default function ConsentGate({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthContext();
  // In-memory acceptance for the current mount — flipped on a successful
  // accept call so the app unblocks immediately without any token refresh.
  const [acceptedThisSession, setAcceptedThisSession] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Explicit-agree state (in memory only — never persisted, never pre-ticked).
  const [agreed, setAgreed] = useState(false);
  const [activeType, setActiveType] = useState<PolicyDocumentType | null>(null);
  const [docMeta, setDocMeta] = useState<Partial<Record<PolicyDocumentType, DocMeta>>>({});

  const pending = user?.consentPending === true;
  const gateActive = pending && !acceptedThisSession && !(user ? hasSessionAck(user.sub) : false);

  // Pre-fetch each document's version / effective date for the checklist. Uses
  // the same cached innovate-service proxy the reader fetches from; a failure
  // here just omits the version line — consent does not depend on the fetch
  // succeeding.
  useEffect(() => {
    if (!gateActive) return;
    let cancelled = false;
    REQUIRED_DOCUMENTS.forEach(({ type }) => {
      fetchCurrentDocument(type)
        .then((document) => {
          if (!cancelled) {
            setDocMeta((prev) => ({
              ...prev,
              [type]: { version: document.version, effectiveAt: document.effectiveAt },
            }));
          }
        })
        .catch(() => {
          // Checklist shows the document without a version line.
        });
    });
    return () => {
      cancelled = true;
    };
  }, [gateActive]);

  const recordMeta = useCallback((type: PolicyDocumentType, document: PolicyDocument) => {
    setDocMeta((prev) => ({
      ...prev,
      [type]: { version: document.version, effectiveAt: document.effectiveAt },
    }));
  }, []);

  if (!gateActive) {
    return <>{children}</>;
  }

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await consentApi.accept();
      if (user) setSessionAck(user.sub);
      setAcceptedThisSession(true);
    } catch {
      // The proxy surfaces AMS failures as retryable 5xx errors — offer retry.
      setError('We could not record your acceptance. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeDoc = activeType
    ? REQUIRED_DOCUMENTS.find(({ type }) => type === activeType)
    : undefined;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-gray-950/5 dark:ring-white/10 p-6 sm:p-8">
        {activeDoc ? (
          <ReaderPane
            key={activeDoc.type}
            type={activeDoc.type}
            label={activeDoc.label}
            meta={docMeta[activeDoc.type]}
            onLoaded={recordMeta}
            onBack={() => setActiveType(null)}
          />
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                <svg
                  className="h-6 w-6 text-indigo-600 dark:text-indigo-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Review and accept our terms
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please review our policies. Open a document to read it, then tick the box below
                to continue.
              </p>
            </div>

            <ul className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700 rounded-md border border-gray-200 dark:border-gray-700">
              {REQUIRED_DOCUMENTS.map(({ type, label }) => {
                const meta = docMeta[type];
                return (
                  <li
                    key={type}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                      {meta && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Version {meta.version} — effective {formatEffectiveDate(meta.effectiveAt)}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`consent-open-${type}`}
                      onClick={() => setActiveType(type)}
                      className="rounded-md border border-indigo-600 px-3 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    >
                      Open
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-start gap-3 text-left">
              <input
                type="checkbox"
                id="consent-agree"
                data-testid="consent-agree-checkbox"
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                aria-labelledby="consent-agree-label"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              <span
                id="consent-agree-label"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setActiveType('terms_of_use')}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Terms of Use
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={() => setActiveType('privacy_policy')}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Privacy Policy
                </button>
              </span>
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400 text-center" role="alert">
                {error}
              </p>
            )}

            <div className="flex flex-col items-center gap-3">
              <button
                onClick={handleAccept}
                disabled={submitting || !agreed}
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Recording…' : 'Agree'}
              </button>
              <button
                onClick={logout}
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
